'use client';
import { Sentiment as SentimentEnum } from '@mnd-frontend/graphql-schema';
import IconSentimentNegative from '@mnd-frontend/icons/components/SentimentNegative';
import IconSentimentNeutral from '@mnd-frontend/icons/components/SentimentNeutral';
import IconSentimentPositive from '@mnd-frontend/icons/components/SentimentPositive';
import IconSparkle from '@mnd-frontend/icons/components/Sparkle';
import IconStarFilled from '@mnd-frontend/icons/components/StarFilled';
import { Tooltip } from '@mnd-frontend/ui/publish/components';
import { useTranslation } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_200,
  GREY_400,
  GREY_800,
  RADIUS_LG,
  RADIUS_XL,
  SIZING_1_5,
  SPACING_2,
} from '@mnd-frontend/ui/shared/tokens';
import styled from 'styled-components';

const Divider = styled.div<{ $isCompactVariant?: boolean }>`
  width: 1px;
  height: ${props => (props.$isCompactVariant ? 'auto' : '34px')};
  margin: ${props => (props.$isCompactVariant ? '-2px 0' : '0')};
  align-self: ${props => (props.$isCompactVariant ? 'stretch' : 'center')};
  background-color: ${GREY_400};
`;

const AISummaryContainer = styled.div<{ $isCompactVariant?: boolean }>`
  display: flex;
  ${media.lessThan('tablet')} {
    flex-direction: column;
  }
  align-items: center;
  gap: ${props => (props.$isCompactVariant ? '8px' : '16px')};
  border-radius: ${props => (props.$isCompactVariant ? RADIUS_LG : RADIUS_XL)};
  background-color: ${GREY_200};
  padding: ${SPACING_2};
  width: 100%;
`;

const RelevanceSentimentContainer = styled.div<{ $isCompactVariant?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => (props.$isCompactVariant ? '8px' : '16px')};
  margin-left: auto;

  ${media.lessThan('tablet')} {
    width: 100%;
    justify-content: space-around;
  }
`;

const RelevanceLabel = styled.div<{ $isCompactVariant?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => (props.$isCompactVariant ? '4px' : '7px')};
  color: ${GREY_800};
  font-size: ${SIZING_1_5};
  font-weight: 500;
`;

const RelevanceIcons = styled.div<{ $isCompactVariant?: boolean }>`
  display: flex;
  flex-direction: row;
  margin-top: ${props => (props.$isCompactVariant ? '0' : '4px')};
`;

const Sentiment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: ${GREY_800};
  font-size: ${SIZING_1_5};
  font-weight: 500;
`;

const AnalysisContainer = styled.div<{ $isCompactVariant?: boolean }>`
  color: ${GREY_800};
  font-size: ${props => (props.$isCompactVariant ? '0.75rem' : '0.875rem')};
  line-height: 1.25;
  flex-grow: 1;
`;

const Text = styled.span`
  font-weight: 400;
`;

const Analysis = styled.span`
  font-weight: 600;
`;

const IconWrapper = styled.span`
  position: relative;
  top: -2px;
`;

export type AISummaryProps = {
  compact?: boolean;
  analysis?: string | null;
  brandSentiment?: SentimentEnum | null;
  brandSentimentDesc?: string | null;
  relevanceLevel?: number | null;
};

const AISummary = ({
  compact = false,
  analysis,
  brandSentiment,
  brandSentimentDesc,
  relevanceLevel,
}: AISummaryProps) => {
  const { t } = useTranslation();

  const relevanceIcons = relevanceLevel
    ? Array(5)
        .fill(null)
        .map((_, index) => (
          <IconStarFilled
            key={index}
            $size="xxs"
            $color={index < relevanceLevel ? 'colorIconsDefault' : 'GREY_500'}
          />
        ))
    : null;

  const getRelevanceTooltipContent = (level: number) => {
    switch (level) {
      case 1:
        return t('mention_card_relevance_tooltip_1_star');
      case 2:
        return t('mention_card_relevance_tooltip_2_stars');
      case 3:
        return t('mention_card_relevance_tooltip_3_stars');
      case 4:
        return t('mention_card_relevance_tooltip_4_stars');
      case 5:
        return t('mention_card_relevance_tooltip_5_stars');
      default:
        return '';
    }
  };

  return (
    <AISummaryContainer $isCompactVariant={compact}>
      {analysis && (
        <>
          <AnalysisContainer $isCompactVariant={compact}>
            <IconWrapper>
              <IconSparkle $size="xxs" />
            </IconWrapper>{' '}
            <Analysis>{t('monitor_mention-card_ai-summary_analysis')}: </Analysis>
            <Text>{analysis}</Text>
          </AnalysisContainer>
          {<Divider $isCompactVariant={compact} />}
        </>
      )}

      <RelevanceSentimentContainer $isCompactVariant={compact}>
        {relevanceLevel && (
          <Tooltip
            content={getRelevanceTooltipContent(relevanceLevel)}
            placement="top"
            maxWidth="220px"
          >
            <RelevanceLabel $isCompactVariant={compact}>
              <RelevanceIcons $isCompactVariant={compact}>{relevanceIcons}</RelevanceIcons>
              {t('monitor_relevance')}
            </RelevanceLabel>
          </Tooltip>
        )}

        {relevanceLevel && brandSentiment && <Divider $isCompactVariant={compact} />}

        {brandSentiment && (
          <Tooltip
            content={brandSentimentDesc}
            placement="top"
            hidden={!brandSentimentDesc}
            maxWidth="220px"
          >
            <Sentiment>
              {brandSentiment === SentimentEnum.Positive && (
                <IconSentimentPositive $size={compact ? 'xxs' : 'xs'} $color="colorIconsSuccess" />
              )}
              {brandSentiment === SentimentEnum.Neutral && (
                <IconSentimentNeutral $size={compact ? 'xxs' : 'xs'} $color="GREY_500" />
              )}
              {brandSentiment === SentimentEnum.Negative && (
                <IconSentimentNegative $size={compact ? 'xxs' : 'xs'} $color="colorIconsDanger" />
              )}
              {t('monitor_mention-card_ai-summary_sentiment')}
            </Sentiment>
          </Tooltip>
        )}
      </RelevanceSentimentContainer>
    </AISummaryContainer>
  );
};

export default AISummary;
